import { RWaveLoader } from '@rgrd/component-library'
import { useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router'

import { useRedeemEmrSessionMutation } from '~/shell/api/rtk/generated'
import { emrSession } from '~/shell/auth/emrSession'

import styles from './LaunchPage.module.css'
import { LAUNCH_ERROR_PATH } from './paths'

/** The query parameter the atlas service's success redirect carries the single-use handoff code in. */
const HANDOFF_CODE_PARAMETER = 'code'

/**
 * Where an EMR launch lands: redeems its handoff code for the session it stands for, then hands over
 * to the bootstrap.
 *
 * This is the frontend half of the SMART launch (Atlas ADR-0002, Atlas ADR-0027). The EHR has already driven the
 * browser through the atlas service and its EMR, which redirects here with a single-use handoff code; the
 * token itself is fetched by `POST /auth/emr/session` so it never enters browser history. Once
 * established, the session is indistinguishable to the rest of the suite from a standalone one: the
 * bootstrap reads the token's `customer` claim and routes to `/c/:customer/`, which is what keeps this
 * redirect target customer-agnostic and out of the EMR's registered-URL list.
 *
 * The route is deliberately outside the login gate, since a launch arrives with no Auth0 session and
 * must never be sent to an interactive login inside the EHR.
 *
 * Every failure lands on the same retryable notice, including a missing or already-redeemed code. The
 * user's only recovery is the EHR's own relaunch, so distinguishing the causes would give them
 * nothing to act on; the reason is logged by the backend, or is the 401 the exchange answered with.
 */
export const LaunchPage = () => {
  const [searchParams] = useSearchParams()
  const handoffCode = searchParams.get(HANDOFF_CODE_PARAMETER)
  const navigate = useNavigate()
  const [redeemEmrSession] = useRedeemEmrSessionMutation()

  // A handoff code is single-use, so the exchange must run once per mount however often React
  // re-renders or re-runs this effect. A second attempt would be answered with a 401 and would fail a
  // launch that had already succeeded.
  const redemptionStarted = useRef(false)

  useEffect(() => {
    if (redemptionStarted.current) {
      return
    }
    redemptionStarted.current = true

    if (handoffCode === null) {
      void navigate(LAUNCH_ERROR_PATH, { replace: true })
      return
    }

    // Marks the browser EMR-embedded before the request goes out, so the exchange itself is sent
    // unauthenticated rather than reaching for the Auth0 session an EMR-embedded browser does not have.
    emrSession.beginLaunch()

    redeemEmrSession({ sessionRequest: { handoff_code: handoffCode } })
      .unwrap()
      .then((session) => {
        emrSession.establish(session.access_token, session.expires_in)
        void navigate('/', { replace: true })
      })
      .catch(() => {
        void navigate(LAUNCH_ERROR_PATH, { replace: true })
      })
  }, [handoffCode, navigate, redeemEmrSession])

  return (
    <main className={styles.loader}>
      <RWaveLoader label="Starting Regard…" />
    </main>
  )
}
